import {
  default as React,
  FunctionComponent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'
import { RouteComponentProps } from 'react-router'
import { distanceInWordsToNow } from 'date-fns'
import useTitle from 'react-use/lib/useTitle'
import { useSelector } from 'react-redux'
import { map, sortBy } from 'lodash/fp'

import useThunkDispatch from '../../common/useThunkDispatch'
import {
  accountUsersPaginationStateSelector,
  accountUsersSelector,
  accountUsersTotalSelector,
  usersAccountSelector,
} from '../accountsSelectors'
import name from '../../common/name'
import {
  FilterAccountUsersReq,
  requestAccountUsers,
  requestUsersAccount,
  impersonateUser,
  generateAppLoginToken,
} from '../accountsActions'
import Splash from '../../components/Splash'
import Avatar from '../../components/Avatar'
import { IUser } from '../../types/user'
import AccountUserEdit from './AccountUserEdit'
import EditIcon from '../../icons/edit.svg'
import ImpersonateIcon from '../../icons/impersonate.svg'
import AppLoginIcon from '../../icons/app-login.svg'
import Search from '../../components/Search'
import AutoSizer from 'react-virtualized-auto-sizer'
import {
  FixedSizeList,
  ListChildComponentProps,
  ListItemKeySelector,
} from 'react-window'
import useDebounce from 'react-use/lib/useDebounce'
import Button from '../../components/Button'
import Pager from '../../components/Pager'
import { currentUserSelector } from '../../auth/authSelectors'

const PER_PAGE = 50

interface IRouteParams {
  accountId: string
}

interface IAppTokenModalState {
  token: string
  userId: number
}

const Row: FunctionComponent<ListChildComponentProps> = ({
  data,
  index,
  style,
}) => {
  const user = data.users[index]
  const isInternal = data.isInternal
  const [loadingWeb, setLoadingWeb] = useState(false)
  const [loadingApp, setLoadingApp] = useState(false)

  const handleWeb = async () => {
    setLoadingWeb(true)
    await data.dispatch(impersonateUser(user.id))
    setLoadingWeb(false)
  }

  const handleAppToken = async () => {
    setLoadingApp(true)
    try {
      const token = await data.dispatch(generateAppLoginToken(user.id))
      data.setAppTokenModal({ token, userId: user.id })
    } catch (e) {
      alert('Failed to generate token')
    }
    setLoadingApp(false)
  }

  const separator = <div className="table_action-separator" />

  return (
    <div style={style}>
      <div className="table_tr">
        <div className="table_td -avatar">
          <Avatar user={user} />
        </div>
        <div className="table_td -g25 ellipsis">{name(user)}</div>
        <div className="table_td -g25 ellipsis">{user.email}</div>
        <div className="table_td -fix150 ellipsis">{user.role}</div>
        <div
          className="table_td -g50 ellipsis"
          title={map('name', user.branches).join(', ')}
        >
          {map('name', user.branches).join(', ')}
        </div>
        <div className="table_td -human-readable-date ellipsis">
          {user.lastSignInAt &&
            distanceInWordsToNow(user.lastSignInAt, {
              addSuffix: true,
            }).replace('about ', '')}
        </div>
        <div className="table_td -fix200 -actions">
          <button
            className="clear-button link"
            onClick={(e) => {
              e.preventDefault()
              data.setEditingUser(user)
            }}
          >
            <EditIcon className="table_edit-link" />
          </button>
          {isInternal && (
            <>
              {separator}
              <span className="table_action-slot">
                {user.role !== 'technician' && (
                  <button
                    className="clear-button"
                    onClick={handleWeb}
                    disabled={loadingWeb}
                    title="Impersonate in web portal"
                  >
                    <ImpersonateIcon
                      style={{ width: 46, height: 16, display: 'block' }}
                    />
                  </button>
                )}
              </span>
              {separator}
              <button
                className="clear-button"
                onClick={handleAppToken}
                disabled={loadingApp}
                title="Generate app login code"
              >
                <AppLoginIcon
                  style={{ width: 44, height: 16, display: 'block' }}
                />
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

const AccountUsers: FunctionComponent<RouteComponentProps<IRouteParams>> = ({
  match,
}) => {
  const accountId = Number(match.params.accountId)
  const dispatch = useThunkDispatch()
  const currentUser = useSelector(currentUserSelector)
  const isInternal = currentUser?.role === 'internal'

  const listRef = useRef<FixedSizeList>(null)

  const [editingUser, setEditingUser] = useState<Partial<IUser> | null>(null)
  const closeUserEdit = useCallback(
    () => setEditingUser(null),
    [setEditingUser],
  )
  const [appTokenModal, setAppTokenModal] =
    useState<IAppTokenModalState | null>(null)
  const [countdown, setCountdown] = useState(300)

  useEffect(() => {
    if (!appTokenModal) return
    setCountdown(300)
    const interval = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(interval)
          return 0
        }
        return prev - 1
      })
    }, 1000)
    return () => clearInterval(interval)
  }, [appTokenModal])

  const refreshUsers = useCallback(async () => {
    await loadUsers({ accountId, first: PER_PAGE, text: term || undefined }, 0)
  }, [accountId])

  const [page, setPage] = useState<number>(0)
  const [term, setTerm] = useState<string | undefined>(undefined)
  const [loading, setLoading] = useState(true)

  const account = useSelector(usersAccountSelector)
  const users: IUser[] = useSelector(accountUsersSelector)
  const paginationState = useSelector(accountUsersPaginationStateSelector)
  const total = useSelector(accountUsersTotalSelector)

  useTitle(`Users - ${name(account)} - Forshaw`)

  const loadUsers = async (req: FilterAccountUsersReq, newPage: number) => {
    setLoading(true)
    await dispatch(requestAccountUsers(req))
    setPage(newPage)
    setLoading(false)
    if (listRef.current) listRef.current.scrollToItem(0)
  }

  const handlePrevious = () => {
    loadUsers(
      {
        accountId,
        last: PER_PAGE,
        before: paginationState.startCursor,
        text: term || undefined,
      },
      page - 1,
    )
  }

  const handleNext = () => {
    loadUsers(
      {
        accountId,
        first: PER_PAGE,
        after: paginationState.endCursor,
        text: term || undefined,
      },
      page + 1,
    )
  }

  useDebounce(
    async () => {
      if (term == null) return
      await loadUsers(
        { accountId, first: PER_PAGE, text: term || undefined },
        0,
      )
    },
    300,
    [term],
  )

  useEffect(() => {
    if (!account || account.id !== accountId) {
      dispatch(requestUsersAccount(accountId))
    }
    loadUsers({ accountId, first: PER_PAGE }, 0)
  }, [accountId])

  const ItemKey: ListItemKeySelector = (index) => users[index].id

  return (
    <div className="container">
      <div className="container table-page-layout">
        {editingUser && account && (
          <AccountUserEdit
            accountId={accountId}
            user={editingUser}
            branches={sortBy('name', account.branches || [])}
            divisions={sortBy('name', account.divisions || [])}
            onClose={closeUserEdit}
            onSave={refreshUsers}
          />
        )}

        {appTokenModal && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0,0,0,0.4)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000,
            }}
          >
            <div
              style={{
                background: '#fff',
                borderRadius: 8,
                padding: 24,
                width: 480,
                boxShadow: '0px 16px 26px 0px rgba(5,19,80,0.07)',
                position: 'relative',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  marginBottom: 24,
                }}
              >
                <span
                  style={{
                    color: '#051350',
                    fontWeight: 500,
                    fontSize: 16,
                    flex: 1,
                  }}
                >
                  Impersonate
                </span>
                <button
                  className="clear-button link"
                  onClick={() => setAppTokenModal(null)}
                  style={{ lineHeight: 0 }}
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path
                      d="M12 4L4 12M4 4l8 8"
                      stroke="#051350"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                    />
                  </svg>
                </button>
              </div>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 24,
                }}
              >
                <div
                  style={{
                    color: '#051350',
                    fontWeight: 500,
                    fontSize: 16,
                    textAlign: 'center',
                  }}
                >
                  Enter this code in the mobile app for impersonation
                </div>
                <div
                  style={{
                    background: '#f2f6fa',
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'center',
                    padding: '12px 16px',
                  }}
                >
                  <span
                    style={{ color: '#051350', fontWeight: 500, fontSize: 19 }}
                  >
                    {appTokenModal.token.split('').join(' ')}
                  </span>
                </div>
                <div
                  style={{
                    color: '#757d9f',
                    fontSize: 13,
                    textAlign: 'center',
                  }}
                >
                  This code will expire in {Math.floor(countdown / 60)}:
                  {(countdown % 60).toString().padStart(2, '0')}
                </div>
              </div>
              <div style={{ paddingTop: 24 }}>
                <Button block onClick={() => setAppTokenModal(null)}>
                  OK
                </Button>
              </div>
            </div>
          </div>
        )}

        <div className="filter">
          <div className="filter_col -large">
            <Search
              placeholder="Search Users"
              value={term}
              onChange={(e) => setTerm(e.target.value)}
            />
          </div>
          <div className="filter_sep" />
          <div className="filter_col">
            <Button onClick={() => setEditingUser({ role: 'technician' })}>
              Create Technician
            </Button>
          </div>
        </div>

        <div className="table">
          <div className="table_head-tr">
            <div className="table_td -avatar" />
            <div className="table_td -g25 table_title">Users</div>
            <div className="table_td -g25 table_title">Email</div>
            <div className="table_td -fix150 table_title">Role</div>
            <div className="table_td -g50 table_title">Branch</div>
            <div className="table_td -human-readable-date table_title ellipsis">
              Last log in
            </div>
            <div className="table_head-tr table_td -fix200 table_title" />
          </div>
        </div>

        <div className="table_loading-wrapper">
          {users.length === 0 && !loading ? (
            <div className="table">
              <div className="table_head-tr">
                <div className="table_head-tr table_td table_title">
                  {!!term && 'No users found, please adjust search criteria'}
                  {!term && 'No users found'}
                </div>
              </div>
            </div>
          ) : (
            <AutoSizer className="table">
              {(size) => (
                <FixedSizeList
                  ref={listRef}
                  className="wonder-scroll"
                  {...size}
                  itemData={{
                    users,
                    setEditingUser,
                    isInternal,
                    dispatch,
                    setAppTokenModal,
                  }}
                  itemCount={users.length}
                  itemSize={54 + 8}
                  itemKey={ItemKey}
                >
                  {Row}
                </FixedSizeList>
              )}
            </AutoSizer>
          )}
          <Splash isLoading={loading} bgColor="#F2F6FA" />
        </div>

        <Pager
          page={page}
          total={total}
          perPage={PER_PAGE}
          previousEnabled={
            paginationState && paginationState.hasPreviousPage && !loading
          }
          nextEnabled={
            paginationState && paginationState.hasNextPage && !loading
          }
          onNext={handleNext}
          onPrevious={handlePrevious}
          className="-right"
        />
      </div>
    </div>
  )
}

export default AccountUsers
